import { type ReactNode, type Ref, useLayoutEffect, useRef, useState } from 'react';
import { Icon } from '../icons/Icon';
import { PersonAvatar } from '../PersonAvatar';
import styles from './NoteEntry.module.css';

/**
 * The Notes section's shared parts (the *Quiet composer, author first* layout of the UI revamp preview), drawn the
 * same on the account page, the contact page, the contact's *Notes and tags* dialog and the lead page.
 */

export interface NoteEntryProps {
    /** The author's name, or `null` for a note brought over from another system. */
    readonly author: string | null;
    /** What an authorless note is credited to — *Imported*. */
    readonly importedLabel: string;
    /** When it was written, as shown, and as an ISO instant for `<time>`. */
    readonly when: string;
    readonly whenIso: string;
    /** The note's ⋯ menu, at the head's end, or nothing. */
    readonly menu?: ReactNode;
    /** The text (`NoteText`), or the edit box in its place. */
    readonly children: ReactNode;
    /** *Edited … by …*, or nothing. */
    readonly edited?: ReactNode;
    /** The question, failures and trace line under the note. */
    readonly footer?: ReactNode;
    readonly 'data-testid'?: string;
}

/**
 * One note: the author's initials (or the import glyph), then a head line with the author's name, when it was
 * written and the ⋯ menu, then the text, then *Edited …*, then whatever the section puts under it. The author and
 * the date come first, so the list can be scanned by who and when before reading a note.
 */
export function NoteEntry({
    author,
    importedLabel,
    when,
    whenIso,
    menu,
    children,
    edited,
    footer,
    'data-testid': testId,
}: NoteEntryProps) {
    return (
        <li className={styles.note} data-testid={testId}>
            <span aria-hidden="true" className={styles.avatar}>
                {author === null ? (
                    <span className={styles.imported}>
                        <Icon name="imported" size="sm" />
                    </span>
                ) : (
                    <PersonAvatar name={author} />
                )}
            </span>
            <div className={styles.content}>
                <div className={styles.head}>
                    <span className={styles.author}>{author ?? importedLabel}</span>
                    <time className={styles.when} dateTime={whenIso}>
                        {when}
                    </time>
                    {menu === undefined || menu === null ? null : <span className={styles.menu}>{menu}</span>}
                </div>
                {children}
                {edited === undefined || edited === null ? null : <p className={styles.edited}>{edited}</p>}
                {footer}
            </div>
        </li>
    );
}

/**
 * A note's text, as text with its line breaks (never HTML), shortened to four lines with *Show more* when longer,
 * and *Show less* once opened. Whether it is longer is measured from the drawn text, so it holds at any width.
 */
export function NoteText({
    text,
    'data-testid': testId,
}: {
    readonly text: string;
    readonly 'data-testid'?: string;
}) {
    const ref = useRef<HTMLParagraphElement>(null);
    const [open, setOpen] = useState(false);
    const [long, setLong] = useState(false);
    // biome-ignore lint/correctness/useExhaustiveDependencies: `text` is read through the drawn element; a new text is re-measured.
    useLayoutEffect(() => {
        const element = ref.current;
        if (element === null || open) {
            return undefined;
        }
        const measure = () => setLong(element.scrollHeight > element.clientHeight + 1);
        measure();
        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const observer = new ResizeObserver(measure);
        observer.observe(element);
        return () => observer.disconnect();
    }, [text, open]);
    return (
        <>
            <p
                className={open ? styles.text : `${styles.text} ${styles.clamped}`}
                data-testid={testId}
                ref={ref}
            >
                {text}
            </p>
            {long || open ? (
                <button
                    aria-expanded={open}
                    className={styles.more}
                    data-testid={`${testId ?? 'note-text'}-more`}
                    onClick={() => setOpen(!open)}
                    type="button"
                >
                    {open ? 'Show less' : 'Show more'}
                </button>
            ) : null}
        </>
    );
}

/**
 * The closed composer: one line that reads *Add a note*, styled as a field, which the section opens into its
 * writing box. Takes a ref, so focus can come back to it when the box closes.
 */
export function NoteComposeLine({
    onOpen,
    ref,
    'data-testid': testId,
}: {
    readonly onOpen: () => void;
    readonly ref?: Ref<HTMLButtonElement>;
    readonly 'data-testid'?: string;
}) {
    return (
        <button className={styles.composeLine} data-testid={testId} onClick={onOpen} ref={ref} type="button">
            <Icon name="notes" size="sm" />
            Add a note
        </button>
    );
}
